import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, limit, onSnapshot, orderBy, query } from 'firebase/firestore';
import { db } from '../../firebase';
import { stripMarkdown } from '../../utils/stripMarkdown';

function safeText(value) {
  return String(value || '').trim();
}

function buildExcerpt(body, max = 240) {
  const text = stripMarkdown(body).replace(/\s+/g, ' ');
  if (!text) return '';
  if (text.length <= max) return text;
  return `${text.slice(0, max).trim()}…`;
}

function SlotReviewShowcase() {
  const [items, setItems] = useState([]);
  const [activeId, setActiveId] = useState('');

  useEffect(() => {
    const q = query(collection(db, 'slotReviews'), orderBy('createdAt', 'desc'), limit(5));

    const unsub = onSnapshot(
      q,
      (snap) => {
        const next = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setItems(next);
        if (!activeId && next.length) {
          setActiveId(next[0].id);
        }
      },
      () => {
        setItems([]);
      }
    );

    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const active = useMemo(() => {
    if (!items.length) return null;
    return items.find((i) => i.id === activeId) || items[0];
  }, [activeId, items]);

  const leftList = useMemo(() => items, [items]);

  if (!items.length) return null;

  return (
    <section className="content-card slot-showcase" aria-label="Slot review videos">
      <div className="slot-showcase-title-row">
        <div className="slot-showcase-title">SLOT REVIEW VIDEOS</div>
        <Link to="/slots" className="slot-showcase-all">
          All reviews →
        </Link>
      </div>

      <div className="slot-showcase-grid">
        <div className="slot-showcase-left" aria-label="Latest slot reviews">
          {leftList.map((item, idx) => {
            const title = safeText(item.title) || 'Slot review';
            const provider = safeText(item.provider);
            const thumb = safeText(item.imageUrl);
            const isActive = active && item.id === active.id;
            const rank = idx + 1;
            const href = `/slots/${item.slug || item.id}`;

            return (
              <div key={item.id} className={`slot-item-wrap ${isActive ? 'active' : ''}`}>
                <div
                  className="slot-item-card"
                  role="button"
                  tabIndex={0}
                  aria-pressed={isActive}
                  onClick={() => setActiveId(item.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setActiveId(item.id);
                    }
                  }}
                >
                  <div className="slot-item-top">
                    <div className="slot-thumb" aria-hidden="true">
                      {thumb ? (
                        <img src={thumb} alt="" loading="lazy" />
                      ) : (
                        <div className="slot-thumb-fallback" />
                      )}
                    </div>
                    <Link to={href} className="slot-open-link" aria-label={`Open ${title}`}>
                      Open →
                    </Link>
                  </div>

                  <div className="slot-item-bottom">
                    <div className="slot-item-main">
                      <div className="slot-item-title">{title}</div>
                      {provider && <div className="slot-item-provider">{provider}</div>}
                    </div>
                    <div className="slot-rank" aria-hidden="true">
                      {rank}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="slot-showcase-right" aria-label="Featured slot review">
          {active && (
            <Link to={`/slots/${active.slug || active.id}`} className="slot-featured-link">
              <div className="slot-featured">
                <div className="slot-featured-media">
                  {safeText(active.imageUrl) ? (
                    <img src={String(active.imageUrl)} alt={safeText(active.title) || 'Slot review'} />
                  ) : (
                    <div className="slot-featured-fallback" />
                  )}
                </div>
                <div className="slot-featured-body">
                  <div className="slot-featured-title">{safeText(active.title) || 'Slot review'}</div>
                  {safeText(active.provider) && (
                    <div className="slot-featured-provider">| {safeText(active.provider).toUpperCase()} |</div>
                  )}
                  {safeText(active.body) && (
                    <div className="slot-featured-excerpt">{buildExcerpt(active.body, 280)}</div>
                  )}
                  <div className="slot-featured-actions">
                    <span className="latest-see-all">Read full review →</span>
                  </div>
                </div>
              </div>
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}

export default SlotReviewShowcase;


